import { SearchResponse } from '@algolia/client-search';
import { database } from '@app/firebase';
import LikeButton from '@components/buttons/LikeButton';
import ProductCard from '@components/cards/ProductCard';
import FilterControls from '@components/filter-controls/FilterControls';
import Hero from '@components/hero/Hero';
import awaitUserLogin from '@components/login/awaitUserLogin';
import SearchInput from '@components/search-input/SearchInput';
import UserContext from '@context/UserContext';
import useFacetSearch, { facetSearchServerSide } from '@hooks/useFacetSearch';
import Sentry from '@integrations/Sentry';
import { ContentBlock } from '@layouts/ContentBlocks';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import MuiPagination from '@mui/material/Pagination';
import Typography from '@mui/material/Typography';
import SortButton from '@ui/components/buttons/SortButton';
import Nl2br from '@ui/components/nl2br/Nl2br';
import UserAvatar from '@ui/components/user-avatar/UserAvatar';
import useBreakpoints from '@ui/hooks/useBreakpoints';
import IconMapMarker from '@ui/icons/imaterial/base/Pin 01.svg';
import makerMetaData from '@ui/utils/metadata/makerMetaData';
import simpleLocation from '@ui/utils/simpleLocation';
import onSavedClickProduct from '@utils/clickHandlers/onSavedClickProduct';
import { addSavedUser, removeSavedUser } from '@utils/savedItems';
import { productToSegmentEventData } from '@utils/segmentAnalytics';
import { makeSeoData } from 'config/next-seo.config';
import { collection, doc, DocumentSnapshot, getDoc, getDocs, query as firestoreQuery, where } from 'firebase/firestore';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import React, { useContext, useEffect, useMemo, useState } from 'react';

import { DefaultFirestoreConverter } from '../../../../types/DefaultFirestoreConverter';
import ErrorPage from '../../_error';
import useStyles from './makers.style';

export const getServerSideProps: GetServerSideProps = async ({ query }) => {
  let maker: DocumentSnapshot<guesthouse.User> = null;

  maker = await getDoc(doc(database, `users/${query.maker}`)) as DocumentSnapshot<guesthouse.User>;

  if (!maker.exists()) {
    const makerDocs = await getDocs(firestoreQuery(
      collection(database, 'users').withConverter(new DefaultFirestoreConverter<guesthouse.User>()),
      where('slug', '==', query.maker)
    ));

    if (makerDocs?.docs?.length) {
      maker = makerDocs.docs[0];
    }
  }

  if (!maker?.exists()) {
    return {
      notFound: true,
    };
  }

  const makerData = maker.data();
  const searchResults = await facetSearchServerSide({
    query,
    extraFacetFilters: {
      'owner.docID': maker.id,
    }
  });

  return {
    props: {
      key: query.maker,
      query,
      results: JSON.stringify(searchResults),
      maker: JSON.stringify(makerData),
      metadata: JSON.stringify(makerMetaData(makerData))
    },
  };
};

function MakerPage(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const router = useRouter();

  const userContext = useContext<UserContext>(UserContext);
  const { md, lg } = useBreakpoints();
  const [sortOption, setSortOption] = useState<string>();
  const [anchorEl, setAnchorEl] = useState(null);

  const initResults = useMemo(() => JSON.parse(props.results), [props.results]) as SearchResponse<guesthouse.Product>;
  const maker = useMemo(() => JSON.parse(props.maker), [props.maker]) as guesthouse.User;
  const metadata = useMemo(() => JSON.parse(props.metadata), [props.metadata]) as guesthouse.MetaData;
  const makerId = maker?.docID;
  const objectPosition = useMemo(() => {
    if (maker?.coverPosition) {
      return `${maker.coverPosition[0]}% ${maker.coverPosition[1]}%`;
    }

    return '50% 50%';
  }, [maker?.coverPosition]);
  const { classes, theme } = useStyles({ objectPosition });

  const {
    results,
    filters,
    query,
    setQuery,
    page,
    setPage,
  } = useFacetSearch({
    query: router.query,
    initResults,
    extraFacetFilters: {
      'owner.docID': makerId,
    },
    sortOption
  });

  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    if (Object.keys(router.query).includes('page')) {
      setPage(Number(router.query['page']));
    }
  }, [router.isReady]);

  const saved = userContext.savedUsers?.ids.indexOf(maker.docID) > -1;

  const titleElement = (
    <Box style={{ marginBottom: theme.spacing(1) }}>
      <Typography
        variant="h3Alt"
        component="h1"
        style={{ margin: theme.spacing(0, 0, 2) }}
      >
        {metadata.h1}
      </Typography>
      {maker.bio && (
        <Typography style={{ margin: theme.spacing(0, 0, 4) }}>
          <Nl2br text={maker.bio} />
        </Typography>
      )}
    </Box>
  );

  const handleSortClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setAnchorEl(null);
  };

  if (props.statusCode >= 400) {
    return (<ErrorPage statusCode={props.statusCode} />);
  }

  return (
    <>
      <Head>
        <title>
          {metadata.pageTitle}
        </title>

        {!maker.published && (
          <meta
            name="robots"
            content="noindex"
          />
        )}
      </Head>
      <NextSeo {...makeSeoData(metadata)} />
      <ContentBlock margin={[0, theme.spacing(3)]}>
        <Hero
          key={maker.docID}
          backgroundColor="#ededed"
          backgroundImage={maker.coverURL}
          className={classes.makerHero}
          contentStyle={{
            width: '100%',
          }}
        >
          <UserAvatar
            user={maker}
            size={180}
            className={classes.avatar}
          />

          <Box
            className={classes.locationWrapper}
          >

            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              marginLeft="auto"

            >

              <Box
                display="flex"
                alignItems="center"
              >
                {maker?.location && (
                  <Box
                    display="flex"
                    alignItems="center"
                    style={{ marginRight: theme.spacing(5) }}
                  >
                    <IconMapMarker className={classes.topIcon} />
                    {simpleLocation(maker.location)}
                  </Box>
                )}
              </Box>
              <Box>
                <LikeButton
                  className={classes.likeButton}
                  large={true}
                  enabled={saved}
                  onClick={async () => {
                    if (userContext.user) {
                      if (saved) {
                        removeSavedUser(userContext.user.uid, maker);
                      } else {
                        addSavedUser(userContext.user.uid, maker);
                      }
                    } else {
                      await awaitUserLogin(userContext)
                        .then((user) => {
                          addSavedUser(user.uid, maker);
                        });
                    }
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Hero>

        {!lg && titleElement}
      </ContentBlock>
      <ContentBlock margin={[6, 0, 12]}>
        <Grid
          container
          spacing={4}
        >
          {!md &&
            <Grid
              item
              xs={12}
            >
              <div style={{ width: '100%', marginBottom: md ? 0 : theme.spacing(1.5) }}>
                <SearchInput
                  placeholder="Search for a product"
                  value={query}
                  onValueChange={(value) => {
                    setQuery(value);
                    setPage(0);
                  }}
                />
              </div>
            </Grid>
          }
          <Grid
            item
            xs={6}
            md={12}
            lg={3}
            style={{ paddingTop: 0 }}
          >
            <FilterControls
              filters={[
                filters.category,
                filters.style,
                filters.color,
                filters.price,
              ]}
              pageReset={() => {
                setPage(0);
              }}
              resultsCount={results.nbHits}
            />
          </Grid>

          {!md &&
            <Grid
              item
              xs={6}
              style={{ paddingTop: 0, paddingLeft: theme.spacing(1.5) }}
            >
              <SortButton
                open={Boolean(anchorEl)}
                handleSortClose={handleSortClose}
                handleSortClick={handleSortClick}
                anchorEl={anchorEl}
                optionSetter={(string) => setSortOption(string)}
                className={classes.sortButtonSmall}
              />
            </Grid>
          }

          <Grid
            item
            xs={12}
            lg={9}
            style={{
              paddingTop: 0,
              position: 'relative'
            }}
          >

            {lg && titleElement}

            {md &&
              <div
                style={{
                  display: 'flex',
                  flexDirection: md ? 'row' : 'column',
                  alignItems: 'center'
                }}
              >
                <Typography
                  style={{
                    marginLeft: theme.spacing(1),
                    marginRight: theme.spacing(4.5),
                  }}
                >
                  {results.nbHits}
                  {' '}
                  {results.nbHits === 1 ? 'ITEM' : 'ITEMS'}
                </Typography>

                <div style={{ width: md ? 300 : '100%', marginBottom: md ? 0 : theme.spacing(1.5) }}>
                  <SearchInput
                    placeholder="Search for a product"
                    value={query}
                    onValueChange={(value) => {
                      setQuery(value);
                      setPage(0);
                    }}
                  />
                </div>
                <SortButton
                  open={Boolean(anchorEl)}
                  handleSortClose={handleSortClose}
                  handleSortClick={handleSortClick}
                  anchorEl={anchorEl}
                  optionSetter={(string) => setSortOption(string)}
                  className={classes.sortButtonLarge}
                />
              </div>
            }

            {
              results.hits?.length
                ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: md ? 'row' : 'column',
                      justifyContent: md ? 'space-between' : 'center',
                      margin: 'auto'
                    }}
                  >
                    {results?.page + 1 === results?.nbPages ?
                      <Typography style={{ margin: theme.spacing(3, 0), fontSize: 14 }}>
                        Showing
                        {' '}
                        {results.page === 0 ?
                          results.nbHits - (results.nbHits - 1)
                          :
                          results.nbHits - (results.nbHits - (results.hitsPerPage * results.page))
                        }
                        {' '}
                        -
                        {' '}
                        {results.nbHits}
                        {' '}
                        of
                        {' '}
                        {results.nbHits}
                      </Typography>
                      :
                      <Typography style={{ margin: theme.spacing(3, 0), fontSize: 14 }}>
                        Showing
                        {' '}
                        {results.hitsPerPage * (results.page + 1) - (results.hitsPerPage - 1)}
                        {' '}
                        -
                        {' '}
                        {results.hitsPerPage * (results.page + 1)}
                        {' '}
                        of
                        {' '}
                        {results.nbHits}
                      </Typography>
                    }
                    {results.nbPages > 1 &&
                      <div>
                        <MuiPagination
                          size={md ? 'medium' : 'small'}
                          count={results.nbPages}
                          page={page === 0 ? 1 : page}
                          style={{ margin: theme.spacing(3) }}
                          onChange={(e, value: number) => {
                            e.preventDefault();
                            setPage(value);
                          }}
                        />
                      </div>
                    }
                  </div>
                ) : (
                  <Typography style={{ margin: theme.spacing(3, 0) }}>
                    No products match your search
                  </Typography>
                )
            }

            <Grid
              container
              spacing={1.5}
            >
              {results?.hits?.length > 0 && results.hits.map((product) => {
                const saved = userContext.savedProducts?.ids.indexOf(product.docID) > -1;

                return (
                  <Grid
                    key={product.docID}
                    item
                    xs={6}
                    md={3}
                  >
                    <ProductCard
                      product={product}
                      saved={saved}
                      onClick={() => {
                        try {
                          window.analytics?.track('Product Clicked', productToSegmentEventData(product));
                        } catch (e) {
                          Sentry.captureException(e);
                        }
                      }}
                      onSavedClick={async (e) => onSavedClickProduct(userContext, saved, product as guesthouse.Product, e)}
                    />
                  </Grid>
                );
              })}
            </Grid>

            {results.nbPages > 1 &&
              <div>
                <MuiPagination
                  size={md ? 'medium' : 'small'}
                  count={results.nbPages}
                  page={page === 0 ? 1 : page}
                  style={{ margin: theme.spacing(3) }}
                  onChange={(e, value: number) => {
                    e.preventDefault();
                    setPage(value);
                  }}
                />
              </div>
            }

            <Divider style={{ margin: theme.spacing(6, 0) }} />

            {maker.shipping_policy && (
              <>
                <Typography
                  variant="h5Alt"
                  style={{ margin: theme.spacing(0, 0, 1) }}
                >
                  Shipping Policy
                </Typography>
                <Typography
                  style={{ marginBottom: theme.spacing(4) }}
                  variant="body2"
                >
                  <Nl2br text={maker.shipping_policy} />
                </Typography>
              </>
            )}

            {maker.return_policy && (
              <>
                <Typography
                  variant="h5Alt"
                  style={{ margin: theme.spacing(0, 0, 1) }}
                >
                  Return Policy
                </Typography>
                <Typography
                  style={{ marginBottom: theme.spacing(4) }}
                  variant="body2"
                >
                  <Nl2br text={maker.return_policy} />
                </Typography>
              </>
            )}
          </Grid>
        </Grid>
      </ContentBlock>
    </>
  );
}

export default MakerPage;
